import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Users, 
  Download, 
  Eye,
  Trophy,
  Briefcase,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Zap,
  Copy,
  School
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentPortfolio, setStudentPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  useEffect(() => {
    // Redirect if not a teacher
    if (user && user.role !== 'teacher' && user.role !== 'admin') {
      toast.error('Teacher access required');
      navigate('/dashboard');
      return;
    }
    
    if (token) {
      fetchDashboard();
    }
  }, [token, user, navigate]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teacher/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.status === 403) {
        toast.error('Teacher access required');
        navigate('/dashboard');
        return;
      }
      
      const data = await res.json();
      setDashboard(data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      toast.error('Failed to load teacher dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (league) => {
    setSelectedLeague(league);
    setStudentsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/teacher/leagues/${league.id}/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStudents(data.students || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      toast.error('Failed to load students');
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchStudentPortfolio = async (student) => {
    setSelectedStudent(student);
    setPortfolioLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/teacher/leagues/${selectedLeague.id}/students/${student.user_id}/portfolio`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      setStudentPortfolio(data);
    } catch (err) {
      console.error('Error fetching portfolio:', err);
      toast.error('Failed to load portfolio');
    } finally {
      setPortfolioLoading(false);
    }
  };

  const handleExportGrades = async (league) => {
    try {
      const res = await fetch(`${API_URL}/api/teacher/leagues/${league.id}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Export failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${league.name}_grades.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Grades exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export grades');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="teacher-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-blue-400" />
              Teacher Dashboard
            </h1>
            <p className="text-slate-400 mt-1">
              Manage your classes and track student progress
            </p>
          </div>
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-sm px-3 py-1">
            <School className="h-4 w-4 mr-1" />
            {dashboard?.total_students || 0} Students • {dashboard?.total_leagues || 0} Classes
          </Badge>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Students</p>
                  <p className="text-2xl font-bold text-white">{dashboard?.total_students || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Classes</p>
                  <p className="text-2xl font-bold text-white">{dashboard?.total_leagues || 0}</p>
                </div>
                <GraduationCap className="h-8 w-8 text-emerald-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Avg Participation</p>
                  <p className="text-2xl font-bold text-white">
                    {dashboard?.leagues?.length > 0 
                      ? Math.round(dashboard.leagues.reduce((acc, l) => acc + l.participation_rate, 0) / dashboard.leagues.length)
                      : 0}%
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-amber-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Points Earned</p>
                  <p className="text-2xl font-bold text-white">
                    {dashboard?.leagues?.reduce((acc, l) => acc + l.total_points, 0) || 0}
                  </p>
                </div>
                <Trophy className="h-8 w-8 text-yellow-400/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Classes Grid */}
        <h2 className="text-xl font-semibold text-white mb-4">Your Classes</h2>
        
        {dashboard?.leagues?.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <GraduationCap className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No classes yet</h3>
              <p className="text-slate-400 mb-4">Create an EDU league to start managing your class</p>
              <Button 
                className="bg-blue-600 hover:bg-blue-500"
                onClick={() => navigate('/leagues')}
              >
                Create a Class
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboard?.leagues?.map((league) => (
              <Card key={league.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white text-lg">{league.name}</CardTitle>
                    <Badge variant="outline" className="border-blue-500/50 text-blue-400">
                      {league.student_count} students
                    </Badge>
                  </div>
                  {league.school_name && (
                    <CardDescription className="text-slate-400">
                      {league.school_name}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-slate-400">Participation</p>
                      <p className={`font-semibold ${league.participation_rate >= 80 ? 'text-emerald-400' : league.participation_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {league.participation_rate}%
                      </p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-slate-400">Portfolios Done</p>
                      <p className="font-semibold text-white">
                        {league.portfolios_complete}/{league.student_count}
                      </p>
                    </div>
                  </div>
                  
                  {/* Invite Code */}
                  <div className="flex items-center justify-between bg-slate-800/30 rounded-lg p-2">
                    <span className="text-slate-400 text-sm">Invite Code:</span>
                    <div className="flex items-center gap-2">
                      <code className="text-emerald-400 font-mono">{league.invite_code}</code>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 w-7 p-0"
                        onClick={() => copyInviteCode(league.invite_code)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-slate-800 hover:bg-slate-700"
                      onClick={() => fetchStudents(league)}
                      data-testid={`view-students-${league.id}`}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Students
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-slate-700"
                      onClick={() => handleExportGrades(league)}
                      data-testid={`export-grades-${league.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Students List Modal */}
      <Dialog open={!!selectedLeague && !selectedStudent} onOpenChange={() => setSelectedLeague(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-400" />
              {selectedLeague?.name} - Students
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {students.length} students enrolled
            </DialogDescription>
          </DialogHeader>

          {studentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No students have joined yet. Share the invite code!
            </div>
          ) : (
            <div className="space-y-2">
              {students.map((student) => (
                <div 
                  key={student.user_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors cursor-pointer"
                  onClick={() => fetchStudentPortfolio(student)}
                  data-testid={`student-row-${student.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      student.rank === 1 ? 'bg-yellow-500 text-black' :
                      student.rank === 2 ? 'bg-slate-400 text-black' :
                      student.rank === 3 ? 'bg-amber-600 text-black' :
                      'bg-slate-700 text-white'
                    }`}>
                      {student.rank}
                    </span>
                    <div>
                      <p className="font-medium text-white">{student.username}</p>
                      <p className="text-slate-400 text-sm">{student.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-white">{student.total_points} pts</p>
                      <p className="text-slate-400 text-sm">V${student.portfolio_value.toLocaleString()}</p>
                    </div>
                    <div className="flex gap-1">
                      {student.portfolio_complete ? (
                        <CheckCircle className="h-5 w-5 text-emerald-400" title="Portfolio complete" />
                      ) : (
                        <XCircle className="h-5 w-5 text-slate-500" title="Portfolio incomplete" />
                      )}
                      {student.has_power_play && (
                        <Zap className="h-5 w-5 text-amber-400" title="Power Play set" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              className="border-slate-700"
              onClick={() => handleExportGrades(selectedLeague)}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Grades
            </Button>
            <Button variant="ghost" onClick={() => setSelectedLeague(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Portfolio Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={() => { setSelectedStudent(null); setStudentPortfolio(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-400" />
              {selectedStudent?.username}'s Portfolio
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedStudent?.email}
            </DialogDescription>
          </DialogHeader>

          {portfolioLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            </div>
          ) : !studentPortfolio?.portfolio ? (
            <div className="text-center py-8 text-slate-400">
              This student hasn't created a portfolio yet.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Portfolio Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-sm">Total Value</p>
                  <p className="text-xl font-bold text-white">
                    V${studentPortfolio.portfolio.total_value.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-sm">Holdings</p>
                  <p className="text-xl font-bold text-white">
                    {studentPortfolio.portfolio.holdings_count}/10
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-sm">Total Points</p>
                  <p className="text-xl font-bold text-emerald-400">
                    {studentPortfolio.total_points}
                  </p>
                </div>
              </div>

              {/* Power Play */}
              {studentPortfolio.portfolio.power_play_symbol && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Zap className="h-5 w-5 text-amber-400" />
                  <span className="text-amber-400">Power Play:</span>
                  <span className="font-bold text-white">{studentPortfolio.portfolio.power_play_symbol}</span>
                </div>
              )}

              {/* Holdings */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2">Holdings</h4>
                <div className="space-y-2">
                  {studentPortfolio.portfolio.holdings.map((holding) => (
                    <div 
                      key={holding.symbol}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        holding.is_power_play ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-slate-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {holding.symbol.slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{holding.symbol}</span>
                            <Badge variant="outline" className="text-xs border-slate-600">
                              {holding.type}
                            </Badge>
                            {holding.is_power_play && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                <Zap className="h-3 w-3 mr-1" />3x
                              </Badge>
                            )}
                          </div>
                          <span className="text-slate-400 text-sm">{holding.units.toFixed(4)} units</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-white">V${holding.value.toLocaleString()}</p>
                        <p className={`text-sm ${holding.gain_loss_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {holding.gain_loss_pct >= 0 ? '+' : ''}{holding.gain_loss_pct.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              className="border-slate-700"
              onClick={() => { setSelectedStudent(null); setStudentPortfolio(null); }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
